import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  Star,
  Users,
  Inbox,
  Car as CarIcon,
  Bell,
  ClipboardList,
  Download,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { api, INR, KM, API } from "../lib/gj";
import { useAuth } from "../lib/auth";

const STATUS_BADGE = {
  pending: "bg-orange-100 text-orange-800 border-orange-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-rose-100 text-rose-800 border-rose-200",
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-[#0A192F]/10 rounded-xl p-5 gj-shadow-card">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-[0.18em] text-[#718096] font-semibold">{label}</div>
      <Icon className="w-4 h-4 text-[#F97316]" />
    </div>
    <div className="font-heading text-3xl font-extrabold text-[#0A192F] mt-2">{value}</div>
  </div>
);

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [listings, setListings] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [onboardings, setOnboardings] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user || !user.id) { nav("/login", { state: { from: "/admin" } }); return; }
    if (user.role !== "admin") { toast.error("Admin access required."); nav("/"); }
  }, [user, loading, nav]);

  const refresh = useCallback(async () => {
    try {
      const [l, d, e, n, ob] = await Promise.all([
        api.get("/admin/listings"),
        api.get("/admin/dealers"),
        api.get("/admin/enquiries"),
        api.get("/admin/notifications"),
        api.get("/admin/onboardings"),
      ]);
      setListings(l.data);
      setDealers(d.data);
      setEnquiries(e.data);
      setNotifications(n.data.items || []);
      setUnread(n.data.unread || 0);
      setOnboardings(ob.data || []);
    } catch (err) {
      console.warn("[admin] refresh failed:", err);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/admin/notifications/mark-read");
      refresh();
    } catch (err) {
      console.warn("[admin] mark-all-read failed:", err);
    }
  }, [refresh]);

  useEffect(() => {
    if (user?.role === "admin") refresh();
  }, [user, refresh]);

  const approve = async (id) => {
    await api.post(`/admin/listings/${id}/approve`);
    toast.success("Listing approved");
    refresh();
  };
  const reject = async (id) => {
    await api.post(`/admin/listings/${id}/reject`);
    toast.success("Listing rejected");
    refresh();
  };
  const feature = async (id, val) => {
    await api.post(`/admin/listings/${id}/feature`, null, { params: { featured: val } });
    toast.success(val ? "Marked as featured" : "Removed from featured");
    refresh();
  };

  if (loading || !user || user.role !== "admin")
    return <div className="p-20 text-center">Loading…</div>;

  const pending = listings.filter((l) => l.status === "pending");
  const approved = listings.filter((l) => l.status === "approved");

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10" data-testid="admin-panel">
      <div className="mb-8">
        <div className="gj-divider-gold mb-3" />
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#0A192F]">
          Admin Control
        </h1>
        <p className="text-[#4A5568] mt-1 text-sm">Approve listings, manage dealers, see all enquiries.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={CarIcon} label="Total Listings" value={listings.length} />
        <StatCard icon={CheckCircle2} label="Approved" value={approved.length} />
        <StatCard icon={Users} label="Dealers" value={dealers.length} />
        <StatCard icon={Inbox} label="Enquiries" value={enquiries.length} />
      </div>

      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="bg-white border border-[#0A192F]/10">
          <TabsTrigger value="pending" data-testid="admin-tab-pending">Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="admin-tab-all">All Listings</TabsTrigger>
          <TabsTrigger value="dealers" data-testid="admin-tab-dealers">Dealers</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">Enquiries</TabsTrigger>
          <TabsTrigger value="onboardings" data-testid="admin-tab-onboardings">
            <ClipboardList className="w-3.5 h-3.5 mr-1.5" />
            Onboardings
            {onboardings.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center text-[10px] font-bold bg-[#0A192F]/10 text-[#0A192F] rounded-full px-1.5 min-w-[18px] h-[18px]">
                {onboardings.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="admin-tab-notifications">
            <Bell className="w-3.5 h-3.5 mr-1.5" />
            Notifications
            {unread > 0 && (
              <span
                data-testid="admin-unread-badge"
                className="ml-2 inline-flex items-center justify-center text-[10px] font-bold bg-[#F97316] text-[#0A192F] rounded-full px-1.5 min-w-[18px] h-[18px]"
              >
                {unread}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          <div className="bg-white border border-[#0A192F]/10 rounded-xl overflow-hidden">
            {pending.length === 0 ? (
              <div className="p-12 text-center text-[#4A5568]">No pending listings.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F3F0EA]/40">
                    <TableHead>Car</TableHead>
                    <TableHead>Dealer</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pending.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded bg-[#F3F0EA] overflow-hidden">
                            {l.images?.[0] && <img src={l.images[0]} alt="" className="w-full h-full object-cover" />}
                          </div>
                          <div>
                            <div className="font-semibold">{l.brand} {l.model}</div>
                            <div className="text-xs text-[#718096]">{l.year} · {KM(l.km_driven)}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{l.dealer_name}</TableCell>
                      <TableCell className="font-bold">{INR(l.price)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white mr-2" onClick={() => approve(l.id)} data-testid={`approve-${l.id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="border-[#9B2C2C]/30 text-[#9B2C2C]" onClick={() => reject(l.id)} data-testid={`reject-${l.id}`}>
                          <XCircle className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="all" className="mt-6">
          <div className="bg-white border border-[#0A192F]/10 rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F3F0EA]/40">
                  <TableHead>Car</TableHead>
                  <TableHead>Dealer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Featured</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listings.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell>
                      <div className="font-semibold">{l.brand} {l.model}</div>
                      <div className="text-xs text-[#718096]">{INR(l.price)}</div>
                    </TableCell>
                    <TableCell>{l.dealer_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_BADGE[l.status] || ""}>{l.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {l.is_featured ? <Badge className="bg-[#F97316] text-[#0A192F]">Featured</Badge> : <span className="text-xs text-[#718096]">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => feature(l.id, !l.is_featured)} data-testid={`feature-${l.id}`}>
                        <Star className={`w-4 h-4 ${l.is_featured ? "fill-[#F97316] text-[#F97316]" : ""}`} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="dealers" className="mt-6">
          <div className="bg-white border border-[#0A192F]/10 rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F3F0EA]/40">
                  <TableHead>Dealer</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>City</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dealers.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>
                      <div className="font-semibold">{d.business_name || d.name}</div>
                      <div className="text-xs text-[#718096]">{d.name}</div>
                    </TableCell>
                    <TableCell>{d.email}</TableCell>
                    <TableCell>{d.phone || "—"}</TableCell>
                    <TableCell>{d.city || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="enquiries" className="mt-6">
          <div className="bg-white border border-[#0A192F]/10 rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F3F0EA]/40">
                  <TableHead>Car</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Message</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enquiries.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium">{e.car_label}</TableCell>
                    <TableCell>{e.name}</TableCell>
                    <TableCell>{e.phone}</TableCell>
                    <TableCell className="max-w-md truncate text-[#4A5568]">{e.message || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="onboardings" className="mt-6">
          <div className="bg-white border border-[#0A192F]/10 rounded-xl overflow-hidden" data-testid="admin-onboardings-panel">
            {onboardings.length === 0 ? (
              <div className="p-12 text-center text-[#4A5568]">No dealer onboardings yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F3F0EA]/40">
                    <TableHead>Business</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Docs</TableHead>
                    <TableHead>Consent</TableHead>
                    <TableHead>When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {onboardings.map((o) => {
                    const uploadedDocs = Object.entries(o.documents || {}).filter(([, v]) => v);
                    return (
                      <TableRow key={o.id} data-testid={`onboarding-row-${o.id}`}>
                        <TableCell>
                          <div className="font-semibold text-[#0A192F]">{o.business_name}</div>
                          <div className="text-xs text-[#718096]">
                            {o.owner_name} · {o.business_type}
                            {o.stock_count != null && ` · stock ${o.stock_count}`}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-[#0A192F]">{o.email}</div>
                          <div className="text-xs text-[#4A5568]">
                            📞 {o.mobile}
                            {o.whatsapp && o.whatsapp !== o.mobile && ` · WA ${o.whatsapp}`}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-[#0A192F]">{o.city}, {o.state}</div>
                          <div className="text-xs text-[#718096] max-w-[180px] truncate">{o.business_address}</div>
                        </TableCell>
                        <TableCell>
                          {uploadedDocs.length === 0 ? (
                            <span className="text-xs text-[#718096]">none</span>
                          ) : (
                            <div className="flex flex-col gap-1">
                              {uploadedDocs.map(([k, v]) => (
                                <a
                                  key={k}
                                  href={`${API}/admin/onboardings/${o.id}/document/${k}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-xs text-[#0A192F] hover:text-[#F97316]"
                                  data-testid={`onboarding-doc-${o.id}-${k}`}
                                >
                                  <Download className="w-3 h-3 mr-1" />
                                  {k.replace("_", " ")} ({Math.round((v.size || 0) / 1024)} KB)
                                </a>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {o.consent?.accepted ? (
                            <div className="space-y-0.5">
                              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">
                                Accepted
                              </Badge>
                              <div className="text-[10px] text-[#718096]">
                                {o.consent.accepted_at
                                  ? new Date(o.consent.accepted_at).toLocaleString("en-IN")
                                  : ""}
                              </div>
                              {o.consent.ip && (
                                <div className="text-[10px] text-[#718096]">IP {o.consent.ip}</div>
                              )}
                            </div>
                          ) : (
                            <Badge variant="outline" className="border-rose-200 text-rose-800">
                              No
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-[#718096]">
                          {o.created_at ? new Date(o.created_at).toLocaleString("en-IN") : ""}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="notifications" className="mt-6">
          <div className="bg-white border border-[#0A192F]/10 rounded-xl overflow-hidden" data-testid="admin-notifications-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#0A192F]/5">
              <div className="text-sm text-[#4A5568]">
                {notifications.length} alerts · {unread} unread
              </div>
              {unread > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={markAllRead}
                  className="border-[#0A192F]/20 text-[#0A192F]"
                  data-testid="mark-all-read-btn"
                >
                  Mark all read
                </Button>
              )}
            </div>
            {notifications.length === 0 ? (
              <div className="p-12 text-center text-[#4A5568]">No notifications yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F3F0EA]/40">
                    <TableHead>Event</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notifications.map((n) => (
                    <TableRow key={n.id} className={n.read ? "" : "bg-[#FFFBE6]/50"}>
                      <TableCell>
                        <Badge variant="outline" className="border-[#0A192F]/15 text-[#0A192F]">
                          {n.event === "dealer_signup" && "Dealer"}
                          {n.event === "new_listing" && "Listing"}
                          {n.event === "new_enquiry" && "Enquiry"}
                          {!["dealer_signup", "new_listing", "new_enquiry"].includes(n.event) && n.event}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium text-[#0A192F]">{n.title}</TableCell>
                      <TableCell className="max-w-md truncate text-[#4A5568]">{n.body || "—"}</TableCell>
                      <TableCell className="text-xs text-[#718096]">
                        {n.created_at ? new Date(n.created_at).toLocaleString("en-IN") : ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
